/**
 * @fileoverview Doctor Dashboard KPI Cards Component
 * @description Displays doctor dashboard key performance indicators in accessible card format
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - Mantine UI with WCAG 2.1 AA accessibility
 */

import { SimpleGrid, Card, Text, Group, Stack, ThemeIcon } from '@mantine/core';
import {
  IconCalendarEvent,
  IconBuilding,
  IconDoor,
  IconCalendarWeek,
  IconCalendarStats,
  IconClock,
} from '@tabler/icons-react';
import type { DoctorDashboardKPIs } from '../../../types/doctor';

/**
 * Props for DoctorKPICards component
 */
export interface DoctorKPICardsProps {
  /** Dashboard KPIs data */
  kpis: DoctorDashboardKPIs;
  
  /** Loading state */
  loading?: boolean;
  
  /** Optional custom class name */
  className?: string;
}

/**
 * Doctor Dashboard KPI Cards Component
 * @description Displays 6 key metrics in accessible card format
 * 
 * @example
 * ```tsx
 * const { data } = useDoctorDashboard();
 * const kpis = extractDashboardKPIs(data);
 * 
 * if (!kpis) return <Skeleton />;
 * return <DoctorKPICards kpis={kpis} />;
 * ```
 */
export function DoctorKPICards({ kpis, loading = false, className }: DoctorKPICardsProps) {
  if (loading) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" {...(className && { className })}>
        {[...Array(6)].map((_, i) => (
          <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
            <Card.Section inheritPadding py="xs">
              <div style={{ height: 100 }} />
            </Card.Section>
          </Card>
        ))}
      </SimpleGrid>
    );
  }

  const kpiCards = [
    {
      id: 'upcoming-sessions',
      label: 'Upcoming Sessions',
      value: kpis.upcomingSessions,
      icon: IconCalendarEvent,
      color: 'blue',
      description: 'Total published sessions scheduled',
      ariaLabel: `${kpis.upcomingSessions} upcoming sessions`,
    },
    {
      id: 'unique-buildings',
      label: 'Buildings',
      value: kpis.uniqueBuildings,
      icon: IconBuilding,
      color: 'teal',
      description: 'Distinct buildings you will visit',
      ariaLabel: `${kpis.uniqueBuildings} buildings`,
    },
    {
      id: 'unique-rooms',
      label: 'Rooms',
      value: kpis.uniqueRooms,
      icon: IconDoor,
      color: 'grape',
      description: 'Distinct rooms assigned to you',
      ariaLabel: `${kpis.uniqueRooms} rooms`,
    },
    {
      id: 'this-week',
      label: 'This Week',
      value: kpis.thisWeekSessions,
      icon: IconCalendarWeek,
      color: 'orange',
      description: 'Sessions scheduled this week',
      ariaLabel: `${kpis.thisWeekSessions} sessions this week`,
    },
    {
      id: 'next-week',
      label: 'Next Week',
      value: kpis.nextWeekSessions,
      icon: IconCalendarStats,
      color: 'pink',
      description: 'Sessions scheduled next week',
      ariaLabel: `${kpis.nextWeekSessions} sessions next week`,
    },
    {
      id: 'nearest-session',
      label: 'Next Session',
      value: kpis.nearestSession ? (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">{kpis.nearestSession.sessionTime}</Text>
          <Text size="xs" c="dimmed">{kpis.nearestSession.room}</Text>
          <Text size="xs" fw={500}>{kpis.nearestSession.building}</Text>
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">No upcoming sessions</Text>
      ),
      icon: IconClock,
      color: 'indigo',
      description: kpis.nearestSession 
        ? `Next session: ${new Date(kpis.nearestSession.date).toLocaleDateString()}`
        : 'No upcoming sessions',
      ariaLabel: kpis.nearestSession
        ? `Next session on ${new Date(kpis.nearestSession.date).toLocaleDateString()} at ${kpis.nearestSession.building}`
        : 'No upcoming sessions',
      isComplex: true,
    },
  ];

  return (
    <SimpleGrid
      cols={{ base: 1, sm: 2, lg: 3 }}
      spacing="lg"
      {...(className && { className })}
      role="region"
      aria-label="Dashboard key performance indicators"
    >
      {kpiCards.map((card) => {
        const Icon = card.icon;
        
        return (
          <Card
            key={card.id}
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            role="article"
            aria-label={card.ariaLabel}
          >
            <Group justify="apart" mb="xs">
              <Text fw={500} size="sm" c="dimmed">
                {card.label}
              </Text>
              <ThemeIcon
                color={card.color}
                variant="light"
                size="lg"
                radius="md"
                aria-hidden="true"
              >
                <Icon size={20} stroke={1.5} />
              </ThemeIcon>
            </Group>

            <Text
              fw={700}
              size={card.isComplex ? 'sm' : 'xl'}
              mt="md"
              mb="xs"
            >
              {card.value}
            </Text>

            <Text size="xs" c="dimmed" lineClamp={2}>
              {card.description}
            </Text>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}

/**
 * Skeleton loader for KPI cards
 * @description Displays loading skeleton matching card layout
 */
export function DoctorKPICardsSkeleton({ className }: { className?: string }) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" {...(className && { className })}>
      {[...Array(6)].map((_, i) => (
        <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="apart" mb="xs">
            <div style={{ height: 20, width: 100, backgroundColor: '#e9ecef', borderRadius: 4 }} />
            <div style={{ height: 36, width: 36, backgroundColor: '#e9ecef', borderRadius: 8 }} />
          </Group>
          <div style={{ height: 32, width: 60, backgroundColor: '#e9ecef', borderRadius: 4, marginTop: 16 }} />
          <div style={{ height: 16, width: '100%', backgroundColor: '#e9ecef', borderRadius: 4, marginTop: 8 }} />
        </Card>
      ))}
    </SimpleGrid>
  );
}

/**
 * Format nearest session date
 * @param dateString - ISO date string
 * @returns Formatted date (e.g., "Tomorrow", "Today", "Mon, Oct 8")
 */
export function formatNearestSessionDate(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  // Reset time parts for comparison
  today.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
}

